import React, { useState, useEffect } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericTabs, { TabOption } from "@ui-components/Tabs/GenericTabs";
import { Reflector } from "./ClusterModal";

interface ClusterStep2Props {
  reflectors: Reflector[];
  selectedReflectors: Reflector[];
  setSelectedReflectors: (val: Reflector[]) => void;
  onNext?: () => void;
  onBack: () => void;
  allSameRegion: boolean;
  selectedReflectorRegions: (string | undefined)[];
}

const STATUS_TABS: TabOption[] = [
  { label: "All", value: "All" },
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const ClusterStep2: React.FC<ClusterStep2Props> = ({ reflectors, selectedReflectors, setSelectedReflectors, allSameRegion, selectedReflectorRegions }) => {
  const [regionFilter, setRegionFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [isOverlayLoading, setIsOverlayLoading] = useState(false);

  useEffect(() => {
    if (reflectors.length === 0) {
      setIsOverlayLoading(true);
      setTimeout(() => setIsOverlayLoading(false), 1000);
    }
  }, [reflectors.length]);

  const filteredReflectors = reflectors.filter((r: Reflector) => {
    // Filter out reflectors that are already in a cluster
    const notInCluster = !r.clusterId && !r.cluster;
    
    // Apply region and status filters
    const regionMatch = !regionFilter || r.data?.region === regionFilter;
    const status = r.statusPeer ? "Active" : "Inactive";
    const statusMatch = statusFilter === "All" || status === statusFilter;
    
    return notInCluster && regionMatch && statusMatch;
  });

  const toggleReflector = (id: string) => {
    if (selectedReflectors.some((r) => r.id === id)) {
      setSelectedReflectors(selectedReflectors.filter((r) => r.id !== id));
    } else if (selectedReflectors.length < 2) {
      const found = reflectors.find((r) => r.id === id);
      if (found) setSelectedReflectors([...selectedReflectors, found]);
    }
  };

  const uniqueRegions = Array.from(new Set(
    reflectors
      .filter(r => !r.clusterId && !r.cluster) // Only reflectors not in clusters
      .map((r) => r.data?.region)
      .filter(Boolean)
  ));
  const regionItems = uniqueRegions as string[];

  return (
    <div className="flex flex-col gap-6">
      {isOverlayLoading && (
        <div
          className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]"
        >
          <h2 className="text-xl font-semibold mb-4 text-white">
            Processing request...
          </h2>
          <div
            className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500"
          />
        </div>
      )}
      <h2 className=" font-semibold mb-2 text-semantic-text-primary text-body">Select two CTX Reflectors <span className="text-sm text-semantic-text-secondary">{`(${selectedReflectors.length}/2 selected)`}</span></h2>
      
      {/* Region validation error */}
      {selectedReflectors.length === 2 && !allSameRegion && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
          <div className="text-red-700 text-sm">
            <strong>Error:</strong> Selected reflectors must be in the same region. 
            Current regions: {selectedReflectorRegions.join(', ')}
          </div>
        </div>
      )}
      
      {/* Success message when valid selection */}
      {selectedReflectors.length === 2 && allSameRegion && (
        <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
          <div className="text-green-700 text-sm">
            <strong>Valid selection:</strong> Both reflectors are in the same region ({selectedReflectorRegions[0]})
          </div>
        </div>
      )}
      <div className="flex flex-row gap-6 mb-2">
        <div className="flex-1">
          <div className="text-semantic-text-primary text-body mb-1">Filter by region</div>
          <GenericDropdown
            items={regionItems}
            value={regionFilter}
            onSelect={setRegionFilter}
            defaultLabel="Select one or more..."
          />
        </div>
        <div className="flex-1">
          <div className=" text-semantic-text-primary text-body mb-1">Filter by status</div>
          <GenericTabs
            tabs={STATUS_TABS}
            activeTab={statusFilter}
            onTabChange={setStatusFilter}
          />
        </div>
      </div>
      <GenericCard isCollapsible={false} className="px-0 py-2 ">
        {filteredReflectors.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-2">
              No reflectors available for cluster creation.
            </div>
            <div className="text-sm text-gray-400">
              Please ensure you have active reflectors configured and not already in a cluster.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredReflectors.map((r: Reflector) => {
              const isSelected = selectedReflectors.some((sel) => sel.id === r.id);
              return (
                <GenericCard
                  key={r.id}
                  variant="primary"
                  isCollapsible={false}
                  hasBorder={true}
                  onClick={() => toggleReflector(r.id)}
                  bodyContent={
                    <div className="relative w-full h-full">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!isSelected && selectedReflectors.length >= 2}
                        className="absolute top-2 right-4 w-5 h-5 cursor-pointer z-10 border border-semantic-border-primary rounded checked:bg-semantic-bg-brand checked:border-semantic-bg-brand transition-colors"
                        onClick={e => e.stopPropagation()}
                        onChange={() => toggleReflector(r.id)}
                      />
                      <div className="w-full">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 items-center min-h-[120px]">
                          <div className="flex flex-col gap-2 justify-start">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-lg text-semantic-text-primary">{r.name}</span>
                            </div>
                            <div className="flex flex-col gap-2 mt-2">
                              <span className="text-body text-semantic-text-secondary font-medium">Region</span>
                              <span className="text-body text-semantic-text-secondary font-medium">License</span>
                              <span className="text-body text-semantic-text-secondary font-medium">Status</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 justify-start h-full">
                            <span className="text-sm text-semantic-text-primary font-semibold mt-[2.5rem]">{r.data?.region || '-'}</span>
                            <span className="text-sm text-semantic-text-primary font-semibold">{r.licenseId ? 'BYOL' : '-'}</span>
                            <span className="mt-1">
                              <GenericTag label={r.statusPeer ? 'Active' : 'Inactive'} variant={r.statusPeer ? 'success' : 'default'} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                >
                
                </GenericCard>
              );
            })}
          </div>
        )}
      </GenericCard>
      
    </div>
  );
};

export default ClusterStep2; 